import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Car, Check, Loader2, Pencil, Phone, Printer, RefreshCw, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import CheckInLinkCell from "@/components/admin/CheckInLinkCell";
import CheckInStatusBadge from "@/components/admin/CheckInStatusBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;
const todayStr = () => new Date().toISOString().slice(0, 10);

const sourceLabel = (s) => {
  if ((s || "").startsWith("external:pitchup")) return "Pitchup";
  if ((s || "").startsWith("external:booking")) return "Booking.com";
  if (s === "admin-manual") return "Manual";
  return "Website";
};

/** Inline vehicle-reg editor — one tap to edit, save on Enter or the tick. */
const VehicleCell = ({ item, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.vehicle_reg || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${item.id}/vehicle`, { vehicle_reg: value });
      toast.success(data.vehicle_reg ? `Vehicle saved: ${data.vehicle_reg}` : "Vehicle removed.");
      setEditing(false);
      onSaved?.();
    } catch {
      toast.error("Couldn't save the vehicle reg.");
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return item.vehicle_reg ? (
      <div className="flex items-center gap-2">
        <span
          className="font-mono text-sm font-bold tracking-[0.08em] bg-[#FFDF00] text-[#0F0E0E] px-2.5 py-1 rounded border border-[#0F0E0E]/20"
          data-testid={`onsite-reg-${item.id}`}
        >
          {item.vehicle_reg}
        </span>
        <button
          type="button"
          onClick={() => { setValue(item.vehicle_reg); setEditing(true); }}
          className="p-1.5 rounded-full text-[#6A5F58] hover:bg-[#DDD1BE]"
          title="Edit vehicle reg"
          data-testid={`onsite-reg-edit-${item.id}`}
        >
          <Pencil size={12} />
        </button>
      </div>
    ) : (
      <button
        type="button"
        onClick={() => { setValue(""); setEditing(true); }}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white text-[10px] font-brand tracking-[0.12em] uppercase transition"
        data-testid={`onsite-reg-add-${item.id}`}
      >
        <Car size={12} /> Add reg
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value.toUpperCase())}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        placeholder="AB12 CDE, XY34 ZZZ"
        maxLength={40}
        autoFocus
        className="h-9 font-mono uppercase text-sm w-48"
        data-testid={`onsite-reg-input-${item.id}`}
      />
      <button
        type="button"
        onClick={save}
        disabled={busy}
        className="p-2 rounded-full bg-[#3B8C4E] text-white hover:bg-[#2E6B3C] disabled:opacity-50"
        title="Save"
        data-testid={`onsite-reg-save-${item.id}`}
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
      </button>
      <button
        type="button"
        onClick={() => setEditing(false)}
        className="p-2 rounded-full text-[#6A5F58] hover:bg-[#DDD1BE]"
        title="Cancel"
        data-testid={`onsite-reg-cancel-${item.id}`}
      >
        <X size={13} />
      </button>
    </div>
  );
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** On-site guest list — everyone staying tonight, with vehicles, arrival state and balances. */
const AdminOnSite = () => {
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkInId, setCheckInId] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/on-site`, { params: { date: d } });
      setData(res);
    } catch {
      toast.error("Couldn't load the on-site list.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const toggleCheckIn = async (item, checkedIn) => {
    setCheckInId(item.id);
    try {
      await axios.post(`${API}/admin/bookings/${item.id}/check-in`, { checked_in: checkedIn });
      toast.success(checkedIn ? `${item.guest_name || "Guest"} marked as arrived.` : "Back to waiting on arrival.");
      await load(date);
    } catch {
      toast.error("Couldn't update check-in.");
    } finally {
      setCheckInId(null);
    }
  };

  const items = data?.items || [];
  // Departure sweep: departed guests (leaving today + deposit settled) sink to
  // the bottom so the people still on the park stand out.
  const activeItems = items.filter((b) => !b.departed);
  const departedItems = items.filter((b) => b.departed);
  const sortedItems = [...activeItems, ...departedItems];
  // Arrears nudge — anyone on the park right now with money left to take,
  // plus departed guests who left owing (chase same-day).
  const owing = activeItems.filter((b) => b.balance > 0);
  const owingTotal = owing.reduce((n, b) => n + b.balance, 0);
  const departedOwing = departedItems.filter((b) => b.balance > 0);
  const departedOwingTotal = departedOwing.reduce((n, b) => n + b.balance, 0);
  const checkinDone = activeItems.filter((b) => b.checkin_submitted).length;
  const checkinMissing = activeItems.length - checkinDone;

  return (
    <div className="max-w-3xl">
      <p className="overline text-[#B76E79]">Who's on the park</p>
      <h3 className="font-serif text-2xl mb-1">On-site guest list</h3>
      <p className="text-sm text-[#6A5F58] mb-4">Everyone staying the selected night — vehicles, arrival state and money still owed.</p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-10 w-44"
          data-testid="onsite-date"
        />
        <button
          type="button"
          onClick={() => setDate(todayStr())}
          className="px-4 h-10 rounded-full border border-[#DDD1BE] text-[10px] font-brand tracking-[0.12em] uppercase text-[#3A322E] hover:bg-[#F5EFE6]"
          data-testid="onsite-today"
        >
          Tonight
        </button>
        <button
          type="button"
          onClick={() => load(date)}
          className="p-2.5 rounded-full border border-[#DDD1BE] text-[#3A322E] hover:bg-[#F5EFE6]"
          title="Refresh"
          data-testid="onsite-refresh"
        >
          <RefreshCw size={14} />
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          disabled={!data || items.length === 0}
          className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition text-[10px] font-brand tracking-[0.12em] uppercase disabled:opacity-40"
          title="Print the on-site list (fire-safety roll call)"
          data-testid="onsite-print"
        >
          <Printer size={13} /> Print list
        </button>
        {data && (
          <span className="text-[11px] uppercase tracking-[0.14em] text-[#6A5F58]" data-testid="onsite-summary">
            {data.count} unit{data.count === 1 ? "" : "s"} on-site · {data.vehicles} vehicle{data.vehicles === 1 ? "" : "s"} recorded · {data.waiting} still to arrive
            {data.departed > 0 ? ` · ${data.departed} departed` : ""}
            {activeItems.length > 0 && (
              <span data-testid="onsite-checkin-tally">
                {" · "}
                <span className="text-[#3B8C4E] font-semibold">{checkinDone} checked in</span>
                {checkinMissing > 0 && <span className="text-[#B3394F] font-semibold"> · {checkinMissing} missing check-in</span>}
              </span>
            )}
          </span>
        )}
      </div>

      {loading && <div className="text-center py-10 text-[#6A5F58]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>}

      {!loading && (owing.length > 0 || departedOwing.length > 0) && (
        <div className="bg-[#B3394F] text-white rounded-xl px-4 py-3 flex items-start gap-3" data-testid="onsite-arrears">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            {owing.length > 0 && (
              <>
                <p className="text-[11px] uppercase tracking-[0.16em] font-semibold">
                  Money still to collect on-park — {gbp(owingTotal)}
                </p>
                <p className="text-xs mt-1 leading-relaxed opacity-90">
                  {owing.map((b) => `${b.guest_name || b.guest_email} (${b.unit_label}) — ${gbp(b.balance)}`).join("  ·  ")}
                </p>
              </>
            )}
            {departedOwing.length > 0 && (
              <div className={owing.length > 0 ? "mt-2.5 pt-2.5 border-t border-white/25" : ""} data-testid="onsite-arrears-departed">
                <p className="text-[11px] uppercase tracking-[0.16em] font-semibold">
                  Left owing — chase today — {gbp(departedOwingTotal)}
                </p>
                <p className="text-xs mt-1 leading-relaxed opacity-90">
                  {departedOwing.map((b) => `${b.guest_name || b.guest_email} (${b.unit_label}) — ${gbp(b.balance)}${b.guest_phone ? ` · ${b.guest_phone}` : ""}`).join("  ·  ")}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && data && items.length === 0 && (
        <div className="text-center py-10 text-[#6A5F58] border border-dashed border-[#DDD1BE] rounded-xl" data-testid="onsite-empty">
          Nobody staying on {data.date} — the park is all yours.
        </div>
      )}

      <div className="space-y-3">
        {sortedItems.map((b) => {
          const leaves = b.end_date === date ? "leaves today" : `until ${b.end_date}`;
          return (
            <div
              key={b.id}
              className={`border rounded-xl p-4 ${b.departed ? "opacity-60 grayscale bg-[#F0EAE0] border-[#D8CFC2]" : "bg-white border-[#DDD1BE]"}`}
              data-testid={`onsite-card-${b.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-medium text-[#14100F]">{b.guest_name || b.guest_email}</p>
                  <p className="text-[11px] text-[#6A5F58] mt-0.5">
                    {b.unit_label} · {b.start_date} → {b.end_date} <span className="text-[#B76E79]">({leaves})</span> · {b.guests} guest{b.guests > 1 ? "s" : ""}{b.dogs ? ` · ${b.dogs} dog${b.dogs > 1 ? "s" : ""}` : ""} · {sourceLabel(b.source)}
                  </p>
                  {b.guest_phone && (
                    <a href={`tel:${String(b.guest_phone).replace(/\s/g, "")}`} className="mt-1.5 inline-flex items-center gap-1 text-xs text-[#14100F] hover:text-[#B76E79]" data-testid={`onsite-phone-${b.id}`}>
                      <Phone size={11} /> {b.guest_phone}
                    </a>
                  )}
                </div>
                <div className="text-right space-y-1.5">
                  {b.departed && (
                    <div>
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold ${b.deposit_status === "charged" ? "bg-[#B76E79] text-white" : "bg-[#6A5F58] text-white"}`}
                        data-testid={`onsite-departed-${b.id}`}
                      >
                        Departed{b.deposit_status === "charged" ? " · deposit charged" : ""}
                      </span>
                    </div>
                  )}
                  {b.balance > 0 ? (
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold bg-[#C89B5F] text-white" data-testid={`onsite-balance-${b.id}`}>
                      Owes {gbp(b.balance)}
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold bg-[#3B8C4E] text-white" data-testid={`onsite-balance-${b.id}`}>
                      Settled
                    </span>
                  )}
                  {!b.departed && (
                    <div>
                      <CheckInStatusBadge complete={b.checkin_submitted} onSite testid={`onsite-checkin-status-${b.id}`} />
                    </div>
                  )}
                  {!b.departed && (
                    <div>
                      {b.checked_in_at ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold bg-[#3B8C4E]/15 text-[#3B8C4E]" data-testid={`onsite-arrived-${b.id}`}>
                          <Check size={10} /> Arrived {new Date(b.checked_in_at).toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit" })}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => toggleCheckIn(b, true)}
                          disabled={checkInId === b.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#3B8C4E] transition text-[9px] font-brand tracking-[0.12em] uppercase disabled:opacity-50"
                          data-testid={`onsite-arrive-${b.id}`}
                        >
                          {checkInId === b.id ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                          Arrived
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[#F0EAE0] grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-[9px] uppercase tracking-[0.18em] text-[#6A5F58] mb-1.5">Vehicle</p>
                  <VehicleCell item={b} onSaved={() => load(date)} />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.18em] text-[#6A5F58] mb-1.5">Online check-in</p>
                  <CheckInLinkCell item={b} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Print-only roll-call sheet — hidden on screen, the only thing visible when printing */}
      {data && items.length > 0 && (
        <div className="onsite-print-sheet" data-testid="onsite-print-sheet">
          <h1>Walnut Lakes Holiday Park</h1>
          <p className="sheet-sub">On-site guest list — {data.date} · Printed {new Date().toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</p>
          <p className="sheet-sub">Main Road, Algarkirk, Boston PE20 2LQ · 07982 844201 · Justina@walnutlakes.co.uk</p>

          <table>
            <thead>
              <tr>
                <th className="tick-col">✓</th>
                <th>Guest names</th>
                <th>Unit</th>
                <th>Vehicle reg</th>
                <th>People</th>
                <th>Dogs</th>
                <th>Phone</th>
                <th>Fishing</th>
              </tr>
            </thead>
            <tbody>
              {activeItems.map((b) => {
                const party = b.checkin_docs?.party || [];
                const others = party.slice(1).map((p) => p.name).filter(Boolean).join(", ");
                const anglers = party.filter((p) => p.fishing).map((p) => (p.name || "").split(" ")[0]).filter(Boolean).join(", ");
                return (
                  <tr key={b.id}>
                    <td className="tick-col" />
                    <td>
                      {b.guest_name || b.guest_email}
                      {others && <span className="sheet-sub-row">Also: {others}</span>}
                    </td>
                    <td>{b.unit_label}</td>
                    <td className="reg-cell">{b.vehicle_reg || "—"}</td>
                    <td>{b.guests}</td>
                    <td>{b.dogs || 0}</td>
                    <td>{b.guest_phone || "—"}</td>
                    <td>{anglers || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="sheet-counts">{activeItems.length} unit{activeItems.length === 1 ? "" : "s"} on-site · {activeItems.reduce((n, b) => n + (b.guests || 0), 0)} people · {activeItems.filter((b) => b.vehicle_reg).length} vehicles recorded</p>

          {departedItems.length > 0 && (
            <>
              <h2>Departed today (deposit settled)</h2>
              <table>
                <tbody>
                  {departedItems.map((b) => (
                    <tr key={b.id} className="departed-row">
                      <td>{b.guest_name || b.guest_email}</td>
                      <td>{b.unit_label}</td>
                      <td className="reg-cell">{b.vehicle_reg || "—"}</td>
                      <td>DEPARTED{b.deposit_status === "charged" ? " · DEPOSIT CHARGED" : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <div className="sheet-signoff">
            <span>Roll call completed by: ______________________________</span>
            <span>Time: ____________</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOnSite;
